/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogClose } from '@angular/material/dialog';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { MatButtonModule } from '@angular/material/button';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { MessageService } from '@src/app/shared/message.service';
import { ErrorHelper } from '@src/app/shared/form/form-error/form-error.component';
import { UntypedFormControl } from '@angular/forms';

export class EditFieldDialogOptions {
    field: Field;
    service: AbstractHttpService;
    id: uuid;
    forceTeamId: uuid;
    constructor(defaults: Partial<EditFieldDialogOptions>) {
        Object.assign(this, defaults);
    }
}

@Component({
    selector: 'app-edit-field-dialog',
    templateUrl: './edit-field-dialog.component.html',
    styleUrls: ['./edit-field-dialog.component.scss'],
    imports: [MatDialogContent, CtlHolderComponent, MatButtonModule, MatDialogClose]
})
export class EditFieldDialogComponent {
    dialogRef = inject(MatDialogRef);
    dialogOptions = inject(MAT_DIALOG_DATA) as EditFieldDialogOptions;
    msgSvc = inject(MessageService);

    control: AppFormControl | GridControl;
    item: AbstractObject;
    ready = false;

    fieldSet = new FieldSet({
        fields: [
            FieldMaker.id(),
            FieldMaker.rev(),
            this.dialogOptions.field,
        ],
        formLayout: LAYOUT_OPTIONS.singleCol,
    });

    config = new FormConfig({
        fieldSet: this.fieldSet,
        service: this.dialogOptions.service,
        mode: 'edit',
    })

    constructor() {
        const dopts = this.dialogOptions;
        if (!dopts.field.control) {
            this.control = dopts.field.makeControl();
        } else {
            this.control = dopts.field.control;
        }
        dopts.service.getOne(dopts.id, null, dopts.forceTeamId).subscribe( item => {
            if (item) {
                this.item = item;
                dopts.field.setValue(item, false);
            }
            this.ready = true;
        })
    }

    save() {
        if (!this.control.valid) {
            const msg = ErrorHelper.getAllMsgs(this.control as UntypedFormControl);
            this.msgSvc.showErrorMessage(msg);
        } else if ( !this.control.dirty) {
            this.dialogRef.close(null);
        } else {
            // teamId rides along so an agent managed record is addressed to the tenant that owns
            // it rather than the one the page is standing in - see AbstractAgentManagedHttpService.
            const update = {
                id: this.item.id,
                revision: this.item.revision,
                teamId: this.item.teamId,
            }
            update[this.dialogOptions.field.name] = this.dialogOptions.field.getFormValue();
            this.dialogOptions.service.put(update, this.dialogOptions.forceTeamId).subscribe(result => {
                this.dialogRef.close(result);
            })
        }
    }
}
